import { useEffect, useState } from 'react';
import { API_ROOT } from '../lib/api';

/**
 * Live price loader.
 *
 * Reads /pricelist/prices.json on first call, caches the result in
 * module scope so subsequent components don't re-fetch. The JSON
 * file is the single source of truth — edits to it go live on the
 * next page load.
 *
 * Schema (v1.1) supports two price shapes per product:
 *   "spannrahmen": 89                       // plain number, no sale
 *   "spannrahmen": {                        // object with optional sale
 *     "base": 89,
 *     "sale": 79,
 *     "saleLabel": { "de": "Sommer-Aktion", "nl": "Zomeractie" },
 *     "saleEnds": "2026-08-31"              // optional ISO date
 *   }
 *
 * usePrices() returns the raw JSON or null while loading.
 * formatPrice() returns null | { display, base, isSale, saleLabel, saleEnds }
 * categoryFromPrice() returns the formatPrice object for the cheapest
 * effective price in a category.
 */

let _cache = null;
let _inflight = null;

const STATIC_FALLBACK_LABELS = {
  from: { nl: 'Vanaf ', de: 'Ab ' },
  instead: { nl: 'in plaats van', de: 'statt' },
};

/**
 * German-style EUR formatter: 1.299 → "1.299,-- €", 1234.5 → "1.234,50 €".
 * Returns null for nullish / non-finite input.
 */
export function formatEUR(n) {
  if (n == null || !Number.isFinite(n)) return null;
  if (Number.isInteger(n)) {
    const s = String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${s},-- €`;
  }
  const fixed = n.toFixed(2);
  const [i, d] = fixed.split('.');
  return `${i.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${d} €`;
}

export function loadPrices() {
  if (_cache) return Promise.resolve(_cache);
  if (_inflight) return _inflight;
  // Prices live in the backend so the admin can edit them via the UI.
  // The backend seeds itself from public/pricelist/prices.json on first
  // boot, so the JSON file remains the canonical bootstrap content.
  _inflight = fetch(`${API_ROOT}/prices`, { cache: 'no-cache' })
    .then((r) => {
      if (!r.ok) throw new Error(`/api/prices HTTP ${r.status}`);
      return r.json();
    })
    .then((j) => { _cache = j; _inflight = null; return j; })
    .catch((err) => {
      _inflight = null;
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[prices] failed to load /api/prices:', err);
      }
      _cache = { prices: {}, labels: STATIC_FALLBACK_LABELS };
      return _cache;
    });
  return _inflight;
}

export function usePrices() {
  const [data, setData] = useState(_cache);
  useEffect(() => {
    if (_cache) return;
    let alive = true;
    loadPrices().then((j) => { if (alive) setData(j); });
    return () => { alive = false; };
  }, []);
  return data;
}

// ---------- internals ----------

function normalizeEntry(entry) {
  if (entry == null) return null;
  if (typeof entry === 'number') return { base: entry, sale: null, label: null, ends: null };
  if (typeof entry === 'object' && typeof entry.base === 'number') {
    return {
      base: entry.base,
      sale: typeof entry.sale === 'number' ? entry.sale : null,
      label: entry.saleLabel || null,
      ends: entry.saleEnds || null,
    };
  }
  return null;
}

function isSaleActive(entry, now = new Date()) {
  if (entry.sale == null || entry.sale >= entry.base) return false;
  if (!entry.ends) return true;
  const endsAt = new Date(entry.ends);
  // Treat saleEnds as "valid through end of that day" inclusive
  endsAt.setHours(23, 59, 59, 999);
  return !Number.isNaN(endsAt.getTime()) && endsAt >= now;
}

function fmtAmount(prefix, n) {
  return `${prefix}${formatEUR(n)}`;
}

function buildPriceInfo(entry, prices, lang) {
  if (!entry) return null;
  const labels = prices.labels?.from || STATIC_FALLBACK_LABELS.from;
  const prefix = labels[lang] || labels.nl;
  const sale = isSaleActive(entry);
  const effective = sale ? entry.sale : entry.base;
  return {
    display: fmtAmount(prefix, effective),
    base: fmtAmount(prefix, entry.base),
    isSale: sale,
    saleLabel: sale && entry.label ? (entry.label[lang] || entry.label.nl) : null,
    saleEnds: sale ? entry.ends : null,
  };
}

// ---------- public helpers ----------

/**
 * Format a product price. Returns null while loading or when unset.
 * On success returns { display, base, isSale, saleLabel, saleEnds }.
 * `display` is the effective price (sale if active, else base).
 * `base` is the original strike-through price (always present).
 */
export function formatPrice(prices, categorySlug, productSlug, lang) {
  if (!prices?.prices) return null;
  const entry = normalizeEntry(prices.prices[categorySlug]?.[productSlug]);
  return buildPriceInfo(entry, prices, lang);
}

/**
 * Cheapest "starting at" price for a whole category, derived as the
 * minimum of all effective prices (sale-adjusted) in that category.
 * Returns the same shape as formatPrice() or null.
 */
export function categoryFromPrice(prices, categorySlug, lang) {
  if (!prices?.prices) return null;
  const bucket = prices.prices[categorySlug];
  if (!bucket) return null;
  const entries = Object.values(bucket)
    .map(normalizeEntry)
    .filter((e) => e && typeof e.base === 'number');
  if (!entries.length) return null;
  // Pick the entry with the lowest *effective* price
  const cheapest = entries.reduce((best, cur) => {
    const eBest = isSaleActive(best) ? best.sale : best.base;
    const eCur  = isSaleActive(cur)  ? cur.sale  : cur.base;
    return eCur < eBest ? cur : best;
  });
  return buildPriceInfo(cheapest, prices, lang);
}

/**
 * Volume-tier table (global, applies to every product). Returns null
 * when tiers are disabled, missing, or empty. Otherwise an object:
 *   { label, fromLabel, unit, tiers: [{ minQty, discount }] }
 * with all strings already localized for `lang`.
 */
export function getVolumeTiers(prices, lang) {
  const v = prices?.volumeTiers;
  if (!v || !v.enabled || !Array.isArray(v.tiers) || v.tiers.length === 0) return null;
  const pick = (obj, fallback) => (obj && (obj[lang] || obj.nl)) || fallback;
  const tiers = [...v.tiers]
    .filter((t) => typeof t.minQty === 'number' && typeof t.discount === 'number')
    .sort((a, b) => a.minQty - b.minQty);
  if (!tiers.length) return null;
  return {
    label:     pick(v.label,     'Volume'),
    fromLabel: pick(v.fromLabel, 'from'),
    unit:      pick(v.unit,      ''),
    tiers,
  };
}

/**
 * Cross-sell bundles. Returns null when disabled or empty. Otherwise:
 *   {
 *     eyebrow, title, ctaLabel, saveLabel, popularLabel, insteadLabel,
 *     items: [{
 *       id, name, tagline, products: [{ category, slug, qty }],
 *       price,            // bundle total (number)
 *       basePrice,        // sum of individual effective prices (number)
 *       save,             // basePrice - price (number)
 *       savePct,          // 0-100 integer
 *       displayPrice,     // "€349"
 *       displayBase,      // "€384"
 *       displaySave,      // "€35"
 *       highlight,        // bool
 *       saleEnds,         // ISO string or null, only if still active
 *     }]
 *   }
 * Bundles whose products reference unknown slugs are silently dropped.
 */
export function getBundles(prices, lang) {
  const b = prices?.bundles;
  if (!b || !b.enabled || !Array.isArray(b.items) || b.items.length === 0) return null;
  const labels = b.labels || {};
  const pick = (obj, fallback) => (obj && (obj[lang] || obj.nl)) || fallback;

  const fmtMoney = (n) => formatEUR(n) || '€0';

  const now = new Date();

  const items = b.items
    .map((bundle) => {
      if (!Array.isArray(bundle.products) || bundle.products.length === 0) return null;
      // Skip expired bundles
      if (bundle.saleEnds) {
        const endsAt = new Date(bundle.saleEnds);
        endsAt.setHours(23, 59, 59, 999);
        if (!Number.isNaN(endsAt.getTime()) && endsAt < now) return null;
      }

      // Build a per-product breakdown for transparent display on the card.
      // Each line: { name (localized), qty, unitPrice, lineTotal, displayUnit, displayLine }
      const breakdown = [];
      let basePrice = 0;
      let resolvable = true;
      for (const p of bundle.products) {
        const entry = normalizeEntry(prices.prices?.[p.category]?.[p.slug]);
        if (!entry) { resolvable = false; break; }
        // Use the catalog *base* price (not sale) so the discount column is
        // the only multiplier — keeps the calculation verifiable for the user.
        const unitPrice = entry.base;
        const qty = typeof p.qty === 'number' && p.qty > 0 ? p.qty : 1;
        const lineTotal = unitPrice * qty;
        basePrice += lineTotal;
        breakdown.push({
          category: p.category,
          slug: p.slug,
          qty,
          unitPrice,
          lineTotal,
          displayUnit: fmtMoney(unitPrice),
          displayLine: fmtMoney(lineTotal),
        });
      }
      if (!resolvable) return null;

      // Discount-driven pricing. Defaults to 10% across all bundles; can be
      // overridden per bundle via the `discount` field in prices.json.
      const discountPct = typeof bundle.discount === 'number' && bundle.discount >= 0 && bundle.discount <= 100
        ? bundle.discount
        : 10;
      // Round DOWN to the nearest multiple of 5 — always in the customer's
      // favour so the *effective* discount stays at or above the configured
      // `discount` percentage. Rounding up would silently lower the actual
      // discount below the promised value, which we never want.
      // Proof: floor(base*(1-d)/5)*5 ≤ base*(1-d) ⇒ price ≤ promised price
      //        ⇒ save ≥ promised save ⇒ effectiveDiscount ≥ d.
      const rawPrice = basePrice * (1 - discountPct / 100);
      const price = Math.floor(rawPrice / 5) * 5;
      if (price <= 0) return null;
      const save = Math.max(0, basePrice - price);
      // Recompute the percentage based on the rounded price so the visible
      // "−X%" pill matches the displayed savings exactly.
      const savePct = basePrice > 0 ? Math.round((save / basePrice) * 100) : 0;

      // Stock handling — only render badge when number is provided (>=0).
      const stockLeft = typeof bundle.stockLeft === 'number' ? bundle.stockLeft : null;
      const soldOut = stockLeft != null && stockLeft <= 0;
      let stockBadge = null;
      if (soldOut) {
        stockBadge = pick(labels.soldOut, 'Sold out');
      } else if (stockLeft != null) {
        const tpl = pick(labels.stockLeft, 'only {n} left');
        stockBadge = tpl.replace('{n}', String(stockLeft));
      }

      // Optional decorative badge ("Bestseller", "Neu", ...). Accepts
      // either a preset key referencing labels.badges, or an inline
      // object { label:{nl,de}, color }.
      let tagBadge = null;
      if (bundle.badge) {
        const presets = labels.badges || {};
        const def = typeof bundle.badge === 'string'
          ? presets[bundle.badge]
          : bundle.badge;
        if (def && def.label) {
          tagBadge = {
            label: pick(def.label, ''),
            color: typeof def.color === 'string' ? def.color : '#F26B1A',
          };
        }
      }

      return {
        id: bundle.id,
        name: pick(bundle.name, bundle.id),
        tagline: pick(bundle.tagline, ''),
        products: bundle.products.map((p) => ({ ...p, qty: p.qty || 1 })),
        breakdown,
        price,
        basePrice,
        save,
        savePct,
        displayPrice: fmtMoney(price),
        displayBase: fmtMoney(basePrice),
        displaySave: fmtMoney(save),
        highlight: !!bundle.highlight,
        saleEnds: bundle.saleEnds || null,
        stockLeft,
        soldOut,
        stockBadge,
        tagBadge,
      };
    })
    .filter(Boolean);

  if (!items.length) return null;

  return {
    eyebrow:      pick(labels.eyebrow,  'Bundles'),
    title:        pick(labels.title,    'Combine and save.'),
    ctaLabel:     pick(labels.cta,      'Request bundle'),
    saveLabel:    pick(labels.save,     'Save'),
    popularLabel: pick(labels.popular,  'Popular'),
    soldOutLabel: pick(labels.soldOut,  'Sold out'),
    subtotalLabel: pick(labels.subtotal, lang === 'de' ? 'Listenpreis' : 'Catalogusprijs'),
    insteadLabel: prices.labels?.instead?.[lang] || (lang === 'de' ? 'statt' : 'in plaats van'),
    // Fine-print disclaimers: bundle pricing only applies for the default
    // configuration (white profile + standard mesh) and includes VAT
    // (21% NL / 19% DE). Shipping cost is country-flat.
    whiteStandardNote: pick(labels.whiteStandard, ''),
    vatNote:           pick(labels.vatNote,       ''),
    shippingNote:      pick(labels.shippingNote,  ''),
    liveCounter: {
      enabled: !!b.liveCounter?.enabled,
      maxDelta: typeof b.liveCounter?.maxDelta === 'number' && b.liveCounter.maxDelta > 0
        ? Math.floor(b.liveCounter.maxDelta) : 2,
    },
    items,
  };
}
